/**
 * ONE source of truth for the DEMO content a freshly inserted website section
 * is prefilled with — the sample faces, sponsor wordmarks and event photos,
 * plus the per-variant texts that used to live in `PAddSection/insertSection`.
 *
 * WHY this module exists: before it, the insert path seeded per-variant TEXT
 * only (`CHILD_VARIANT_PROPS` knew `textName`/`textOccupation`, never an
 * image), so every seeded Speaker card carried the same photo, every sponsor
 * the same logo and every gallery tile the same picture — while the palette's
 * preview tile promised four different ones. Both sides now read THESE arrays,
 * so the tile and the inserted section cannot drift apart.
 *
 * Rotation is by INDEX and deliberately deterministic (`list[i % list.length]`)
 * — no `Math.random`, so snapshots, fixtures and e2e stay reproducible.
 *
 * NOT to be confused with the placeholder a BOUND section falls back to: when
 * a real EventSpeaker has no photo, the refresher writes
 * `NEUTRAL_SPEAKER_PLACEHOLDER` (an anonymous silhouette), never a sample
 * face — a demo person under a real speaker's name reads as that person's
 * photo. See `children/Speaker/Speaker.tsx`.
 */

const A = import.meta.env.VITE_ASSET_BASE_URL;

/** Key namespace of the sample art (uploaded via scripts/mirror-wb-assets.sh
 *  from the repo-committed source in assets/wb-templates/shared/samples).
 *  The legacy flat keys (shared/speaker07.jpg, shared/events01.jpg, …) are
 *  deliberately NOT reused: they are baked into pages customers already
 *  saved and published, and swapping their bytes would change live sites. */
const SAMPLES = `${A}/shared/samples`;

/** 12 portraits — the Speaker card renders `aspect-square`, these are 640². */
export const SAMPLE_SPEAKER_IMAGES: string[] = Array.from(
  { length: 12 },
  (_, i) => `${SAMPLES}/speaker-${String(i + 1).padStart(2, "0")}.webp`,
);

/** 8 neutral placeholder wordmarks (invented brands, monochrome SVG). */
export const SAMPLE_SPONSOR_LOGOS: { src: string; alt: string }[] = [
  { src: `${SAMPLES}/sponsor-01.svg`, alt: "Nordis" },
  { src: `${SAMPLES}/sponsor-02.svg`, alt: "Kestra" },
  { src: `${SAMPLES}/sponsor-03.svg`, alt: "Lumea" },
  { src: `${SAMPLES}/sponsor-04.svg`, alt: "Arvex" },
  { src: `${SAMPLES}/sponsor-05.svg`, alt: "Solven" },
  { src: `${SAMPLES}/sponsor-06.svg`, alt: "Halden" },
  { src: `${SAMPLES}/sponsor-07.svg`, alt: "Corvix" },
  { src: `${SAMPLES}/sponsor-08.svg`, alt: "Tavora" },
];

/** Event photography for Gallery/Image children — the 9 seeded `events*`
 *  keys (events09/11/12 were never uploaded; enumerating them would 404). */
export const SAMPLE_EVENT_PHOTOS: string[] = [
  1, 2, 3, 4, 5, 6, 7, 8, 10,
].map((n) => `${A}/shared/events${String(n).padStart(2, "0")}.jpg`);

/** Anonymous silhouette for a REAL speaker without a photo (bound sections)
 *  — never a sample face. */
export const NEUTRAL_SPEAKER_PLACEHOLDER = `${SAMPLES}/speaker-neutral.svg`;

/**
 * Is this URL one of ours (sample art or the legacy stock logo), i.e. demo
 * filler rather than something the customer put there?
 *
 * Consumed by the craft→blocks reducer, which must not export seeded demo
 * logos as if they were real sponsors. Substring match on the key namespace,
 * because the asset base differs per environment (localstack / e2e / prod).
 */
export function isSampleImage(url?: string | null): boolean {
  if (!url) return false;
  return url.includes("/shared/samples/") || url.endsWith("/shared/logo.svg");
}

// ── Per-variant demo content ────────────────────────────────────────────────

/** Text props seeded per variant, keyed by the child's craft displayName.
 *  (Moved here from PAddSection/insertSection.tsx — one table, not two.) */
const CHILD_VARIANT_PROPS: Record<string, string[]> = {
  AgendaChild: ["textText1", "textText2", "textText3", "textText"],
  BlogChild: ["textTopic", "textContent"],
  FaQChild: ["textQuestion", "textAnswer"],
  SpeakerChild: ["textName", "textOccupation"],
  TestimonialsChild: ["textContent", "textName", "textOccupation"],
  NumbersChild: ["textNumber", "textDescription"],
};

/** displayName → `websiteBuilderDefaults` key prefix. */
const CHILD_VARIANT_KEYS: Record<string, string> = {
  AgendaChild: "agendaChild",
  BlogChild: "blogChild",
  FaQChild: "faqChild",
  SpeakerChild: "speakerChild",
  TestimonialsChild: "testimonialsChild",
  NumbersChild: "numbersChild",
};

/**
 * How many text variants each child kind ships in the locale files. Speaker
 * and Testimonials carry 8 (so the 5th added card is not a copy of the 1st);
 * the rest still have 4 and wrap after that.
 */
const CHILD_VARIANT_COUNT: Record<string, number> = {
  AgendaChild: 4,
  BlogChild: 4,
  FaQChild: 4,
  SpeakerChild: 8,
  TestimonialsChild: 8,
  NumbersChild: 4,
};

/** Minimal shape of next-intl's translator — `raw` is what the defaults need
 *  (the values carry HTML, e.g. `<b>…</b>` in agenda/numbers rows). */
export type SampleTranslator = { raw: (key: string) => unknown };

/**
 * Everything a demo child of `displayName` should carry at slot `index`
 * (0-based): the variant texts AND the rotating image props.
 *
 * Returns `{}` for kinds without demo content, so callers can spread it
 * unconditionally.
 */
export function sampleChildProps(
  displayName: string | undefined,
  index: number,
  t: SampleTranslator,
): Record<string, unknown> {
  if (!displayName) return {};
  const slot = Math.max(0, Math.floor(index));
  const props: Record<string, unknown> = {};

  const variantKey = CHILD_VARIANT_KEYS[displayName];
  if (variantKey) {
    // Locale keys are 1-based (`…_variant1_…`).
    const variant = (slot % (CHILD_VARIANT_COUNT[displayName] ?? 4)) + 1;
    for (const prop of CHILD_VARIANT_PROPS[displayName] ?? []) {
      props[prop] = t.raw(`${variantKey}_variant${variant}_${prop}`);
    }
  }

  switch (displayName) {
    case "SpeakerChild":
      props.imageUrl = SAMPLE_SPEAKER_IMAGES[slot % SAMPLE_SPEAKER_IMAGES.length];
      break;
    case "SponsorsChild": {
      const logo = SAMPLE_SPONSOR_LOGOS[slot % SAMPLE_SPONSOR_LOGOS.length];
      props.src = logo.src;
      props.alt = logo.alt;
      break;
    }
    case "ImageChild":
      props.src = SAMPLE_EVENT_PHOTOS[slot % SAMPLE_EVENT_PHOTOS.length];
      break;
    case "TestimonialsChild":
      // Top slot = the quoted person's company mark, bottom slot = their face.
      props.imageUrl =
        SAMPLE_SPONSOR_LOGOS[slot % SAMPLE_SPONSOR_LOGOS.length].src;
      props.imageUrlBottom =
        SAMPLE_SPEAKER_IMAGES[slot % SAMPLE_SPEAKER_IMAGES.length];
      break;
    default:
      break;
  }

  return props;
}

/**
 * The image prop whose value identifies WHICH sample slot a child currently
 * shows — used to pick a non-colliding slot for a newly added item (see
 * `useNextSampleIndex`). Kinds absent here have no rotating image.
 */
export const SAMPLE_IMAGE_PROP: Record<string, string> = {
  SpeakerChild: "imageUrl",
  SponsorsChild: "src",
  ImageChild: "src",
  TestimonialsChild: "imageUrlBottom",
};

/** The rotation a child kind draws from (same order as `sampleChildProps`). */
export function sampleImageList(displayName: string): string[] {
  switch (displayName) {
    case "SpeakerChild":
    case "TestimonialsChild":
      return SAMPLE_SPEAKER_IMAGES;
    case "SponsorsChild":
      return SAMPLE_SPONSOR_LOGOS.map((l) => l.src);
    case "ImageChild":
      return SAMPLE_EVENT_PHOTOS;
    default:
      return [];
  }
}

/**
 * First rotation slot whose image is not already on one of `usedImages`,
 * falling back to `fallbackIndex` (the child count) once every slot is taken.
 *
 * WHY not just the child count: deleting the middle card and adding a new one
 * would hand out a slot a sibling already occupies — two identical demo
 * speakers, which is exactly the defect this whole change removes.
 */
export function nextSampleIndex(
  displayName: string,
  usedImages: (string | undefined)[],
  fallbackIndex: number,
): number {
  const list = sampleImageList(displayName);
  if (list.length === 0) return fallbackIndex;
  const used = new Set(usedImages.filter((u): u is string => !!u));
  const free = list.findIndex((url) => !used.has(url));
  return free === -1 ? fallbackIndex : free;
}
